import React from "react";

import { useTable } from "@refinedev/react-table";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ARTIST_AUDIT_KIND_HINT,
  ARTIST_AUDIT_SEVERITY_OPTIONS,
  ARTIST_AUDIT_STATUS_OPTIONS,
  isArtistAuditKind,
  isArtistAuditSeverity,
  isArtistAuditStatus,
} from "@/lib/tina/artist-audit";
import { ApproveDeceasedDialog } from "@/pages/tina/audits/artist/components/approve-deceased-dialog";
import { DismissFindingDialog } from "@/pages/tina/audits/artist/components/dismiss-finding-dialog";
import { KindCountStrip } from "@/pages/tina/audits/artist/components/kind-count-strip";
import { useArtistAuditColumns } from "@/pages/tina/audits/artist/hooks/use-artist-audit-columns";
import {
  ARTIST_AUDIT_QUERY_ROOT,
  useArtistAuditRepository,
} from "@/pages/tina/audits/artist/hooks/use-artist-audit-repository";
import {
  type ArtistAuditFilterState,
  buildArtistAuditFilters,
} from "@/pages/tina/audits/artist/lib/artist-audit-filters";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import type {
  ArtistAuditFindingRow,
  ArtistAuditRunResponse,
} from "@/types/tina";

const ANY_SEVERITY = "__any_severity__";

/**
 * The Artist tab of `/tina/audits` — the artist duplicate auditor's review queue.
 *
 * **What it is for.** Four deterministic checks run daily over every artist document and
 * file the pairs that are almost certainly one act: two records on one Spotify profile,
 * two records with one name and two profiles, and two shapes of broken merge. On
 * 2026-09-15 that was 123 identity pairs and 1,239 name pairs, 155 of them high.
 *
 * **Dismissal is the only action, and it does NOT fix a duplicate.** Only a merge does,
 * and the merge is made on the artist's own page — every artist name here links to it.
 * Dismiss means "these are two different acts, stop showing me this". A dismissed true
 * duplicate stays in the corpus. Spec section 9.2 and 9.3.
 *
 * All narrowing is server-side, exactly as on the Venue tab.
 */
export const ArtistAuditTab = () => {
  const repo = useArtistAuditRepository();

  const [persistedStatus, setStatus] = usePersistedState<string | null>(
    "tina-artist-audit:status",
    "open",
  );
  const [persistedKind, setKind] = usePersistedState<string | null>(
    "tina-artist-audit:kind",
    null,
  );
  const [persistedSeverity, setSeverity] = usePersistedState<string | null>(
    "tina-artist-audit:severity",
    null,
  );
  // Narrowed on read rather than trusted: these come back from localStorage, and all three
  // params are Literals on the route — an unknown value is a 422, not an empty page.
  const status = isArtistAuditStatus(persistedStatus)
    ? persistedStatus
    : "open";
  const kind = isArtistAuditKind(persistedKind) ? persistedKind : null;
  const severity = isArtistAuditSeverity(persistedSeverity)
    ? persistedSeverity
    : null;

  const [pendingDismissal, setPendingDismissal] =
    React.useState<ArtistAuditFindingRow | null>(null);
  const [dismissingId, setDismissingId] = React.useState<string | null>(null);
  const [pendingApproval, setPendingApproval] =
    React.useState<ArtistAuditFindingRow | null>(null);
  const [approvingId, setApprovingId] = React.useState<string | null>(null);
  const [runOutcome, setRunOutcome] =
    React.useState<ArtistAuditRunResponse | null>(null);
  const [running, setRunning] = React.useState(false);

  const onDismiss = React.useCallback(
    (row: ArtistAuditFindingRow) => setPendingDismissal(row),
    [],
  );
  const onApprove = React.useCallback(
    (row: ArtistAuditFindingRow) => setPendingApproval(row),
    [],
  );
  const columns = useArtistAuditColumns({
    onDismiss,
    dismissingId,
    onApprove,
    approvingId,
  });

  const filterState: ArtistAuditFilterState = { status, kind, severity };
  const table = useTable<ArtistAuditFindingRow>({
    columns,
    refineCoreProps: {
      // Resolved by `RESOURCE_PATHS` to `/artist-audit/findings`.
      resource: "tina-artist-audit",
      pagination: { pageSize: 50 },
      filters: { permanent: buildArtistAuditFilters(filterState) },
    },
  });

  const setCurrentPage = table.refineCore.setCurrentPage;
  const total = table.refineCore.tableQuery.data?.total;

  // Reset to page 1 whenever the narrowing changes.
  const previousScope = React.useRef(`${status}|${kind}|${severity}`);
  React.useEffect(() => {
    const scope = `${status}|${kind}|${severity}`;
    if (previousScope.current === scope) return;
    previousScope.current = scope;
    setCurrentPage(1);
  }, [status, kind, severity, setCurrentPage]);

  const countsQuery = useQuery({
    queryKey: [ARTIST_AUDIT_QUERY_ROOT, "counts", status, severity],
    queryFn: () => repo.countsByKind({ status, severity }),
  });

  async function confirmDismissal(note: string): Promise<void> {
    const finding = pendingDismissal;
    if (!finding) return;
    setDismissingId(finding.id);
    try {
      const result = await repo.dismiss(finding.id, note.trim() || null);
      if (result) setPendingDismissal(null);
    } catch {
      // Surfaced by the repository; the dialog stays open so nothing is lost.
    } finally {
      setDismissingId(null);
    }
  }

  async function confirmApproval(): Promise<void> {
    const finding = pendingApproval;
    if (!finding) return;
    setApprovingId(finding.id);
    try {
      const result = await repo.approve(finding);
      if (result) setPendingApproval(null);
    } catch {
      // Surfaced by the repository; the dialog stays open so it can be retried.
    } finally {
      setApprovingId(null);
    }
  }

  async function onRunNow(): Promise<void> {
    if (running) return;
    setRunning(true);
    try {
      setRunOutcome(await repo.runNow());
      await countsQuery.refetch();
    } catch {
      // Surfaced by the repository.
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            Artist audit
            {typeof total === "number" ? (
              <span className="ml-2 font-mono text-base tabular-nums text-muted-foreground">
                {total}
              </span>
            ) : null}
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            To fix one, open either artist and merge the other into it.{" "}
            <strong className="font-medium text-foreground">
              Dismissing does not merge anything.
            </strong>{" "}
            Use it when they really are two acts. A dismissed pair comes back
            only if either artist&apos;s Spotify link or merge state changes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={(value) => setStatus(value)}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ARTIST_AUDIT_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={severity ?? ANY_SEVERITY}
            onValueChange={(value) =>
              setSeverity(value === ANY_SEVERITY ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[160px]">
              <SelectValue placeholder="Any severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_SEVERITY}>Any severity</SelectItem>
              {ARTIST_AUDIT_SEVERITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="h-9"
            onClick={() => void onRunNow()}
            disabled={running}
            title="Starts the audit job: it re-reads every artist document and re-runs the five checks. Writes findings only, never an artist. Takes a few minutes, and this page does not update on its own — reload it when the run is done."
          >
            {running ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Run audit now
          </Button>
        </div>
      </div>

      <KindCountStrip
        counts={countsQuery.data}
        isLoading={countsQuery.isLoading}
        selected={kind}
        onSelect={(next) => setKind(next)}
      />

      <p className="max-w-4xl text-xs text-muted-foreground">
        {kind
          ? ARTIST_AUDIT_KIND_HINT[kind]
          : "Pick a kind to work one question at a time. High means one Spotify profile on two records, or a multi-word name with both sides booked."}
      </p>

      {runOutcome ? (
        <div
          className={
            runOutcome.kind === "dispatched"
              ? "rounded-md border bg-muted/40 p-3 text-sm"
              : "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm"
          }
        >
          {runOutcome.kind === "dispatched" ? (
            <span>
              <strong className="font-medium">Audit started.</strong> It reads
              every artist document and re-runs the five checks, which takes a
              few minutes. Nothing on this page updates on its own — reload it
              when the run is done.
            </span>
          ) : runOutcome.kind === "noop" ? (
            <span>The audit job is not configured on this environment.</span>
          ) : (
            <span>
              <strong className="font-medium">
                Could not start the audit.
              </strong>{" "}
              {runOutcome.error_message ?? "Cloud Run gave no reason."}
            </span>
          )}
        </div>
      ) : null}

      <DataTable table={table} />

      <ApproveDeceasedDialog
        finding={pendingApproval}
        pending={approvingId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingApproval(null);
        }}
        onConfirm={() => void confirmApproval()}
      />

      <DismissFindingDialog
        finding={pendingDismissal}
        pending={dismissingId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDismissal(null);
        }}
        onConfirm={(note) => void confirmDismissal(note)}
      />
    </div>
  );
};
